'use client';
import React, { useState } from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

export default function TrackPage() {
  const [trackType, setTrackType] = useState<'application' | 'passport' | 'visa'>('application');
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<null | 'found' | 'notfound'>(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setResult(query.length > 3 ? 'found' : 'notfound');
    }, 1500);
  };

  const trackTypes = [
    { id: 'application', label: 'رقم الطلب', placeholder: 'أدخل رقم الطلب (APP-XXXXXXXX)' },
    { id: 'passport', label: 'رقم جواز السفر', placeholder: 'أدخل رقم جواز السفر' },
    { id: 'visa', label: 'مرجع الاستعلام', placeholder: 'أدخل مرجع الاستعلام' },
  ];

  const currentType = trackTypes.find(t => t.id === trackType)!;

  return (
    <div className="min-h-screen flex flex-col font-tajawal" dir="rtl">
      <Navbar activePage="/track" />
      <main className="flex-1 bg-background">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary to-accent py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-white border border-white/20 text-xs font-medium mb-4">
              🔍 استعلام حالة الطلب
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-3">تتبع طلبك</h1>
            <p className="text-white/70 max-w-xl mx-auto text-sm">
              استعلم عن حالة طلبك برقم الطلب أو رقم جواز السفر أو مرجع الاستعلام
            </p>
          </div>
        </div>

        {/* Track Form */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="bg-white rounded-2xl border border-border shadow-gov-lg p-8">
            {/* Type Selector */}
            <div className="flex rounded-xl overflow-hidden border border-border mb-6">
              {trackTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => { setTrackType(type.id as typeof trackType); setResult(null); setQuery(''); }}
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                    trackType === type.id
                      ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSearch}>
              <label className="block text-sm font-semibold text-foreground mb-2">
                {currentType.label}
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setResult(null); }}
                  placeholder={currentType.placeholder}
                  className="input-gov flex-1"
                />
                <button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="gov-btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  بحث
                </button>
              </div>
            </form>

            {/* Result */}
            {result === 'found' && (
              <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-green-800">تم العثور على طلبك</div>
                    <div className="text-xs text-green-600">رقم الطلب: {query}</div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'حالة الطلب', value: 'قيد المراجعة', color: 'text-yellow-700 bg-yellow-100' },
                    { label: 'تاريخ التقديم', value: '2026/01/15', color: '' },
                    { label: 'نوع الخدمة', value: 'تأشيرة دخول', color: '' },
                    { label: 'متوقع الانتهاء', value: '3-5 أيام عمل', color: '' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className={`font-semibold ${item.color ? `px-2 py-0.5 rounded-full text-xs ${item.color}` : 'text-foreground'}`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result === 'notfound' && (
              <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 text-center">
                <svg className="w-10 h-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="font-bold text-red-800 mb-1">لم يتم العثور على طلب</div>
                <div className="text-sm text-red-600">تأكد من صحة البيانات أو تواصل مع الدعم</div>
              </div>
            )}
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
            {[
              { icon: '📎', title: 'رقم الطلب', desc: 'يبدأ بـ APP- ويتكون من 8 أرقام' },
              { icon: '📖', title: 'رقم الجواز', desc: 'رقم جواز السفر المكون من 8 خانات' },
              { icon: '🔑', title: 'مرجع الاستعلام', desc: 'الرمز المرسل عبر البريد الإلكتروني' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-xl border border-border p-4 text-center">
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="font-semibold text-sm text-foreground mb-1">{item.title}</div>
                <div className="text-xs text-muted-foreground">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
